import { useEffect, useReducer, useState } from 'react';
import type { ChangeEvent, FocusEvent } from 'react';

import {
  ComboBox,
  createUID,
  Flex,
  FormDialog,
  Input,
  SummaryList,
  Text
} from '@pega/cosmos-react-core';
import type { FormDialogProps, SummaryListItem } from '@pega/cosmos-react-core';

import { loadingTimeoutMS } from '../Progress/Progress.mocks';

export type MockCategory = `Category ${number}`;

export interface MockItem {
  readonly id: string;
  name: string;
  category: MockCategory;
}

export type MockReducerPayload =
  | {
      type: 'add';
      item: Pick<MockItem, 'name' | 'category'>;
    }
  | {
      type: 'edit';
      item: MockItem;
    }
  | {
      type: 'remove';
      item: Pick<MockItem, 'id'>;
    };

export interface MockItemDialogProps extends Pick<FormDialogProps, 'target'> {
  item: MockItem | null;
  dispatch: (fieldValues: Pick<MockItem, 'name' | 'category'>) => void;
  onComplete: () => void;
}

const errorText = 'Cannot be blank';

const categories = Array.from({ length: 5 }, (_, i): MockCategory => `Category ${i + 1}`);

const initialItems: MockItem[] = Array.from({ length: 3 }, (_, i) => {
  return {
    id: createUID(),
    name: `Editable item ${i + 1}`,
    category: categories[i]
  };
});

const itemReducer = (items: MockItem[], payload: MockReducerPayload) => {
  switch (payload.type) {
    case 'add': {
      return [...items, { ...payload.item, id: createUID() }];
    }

    case 'edit': {
      return items.map(item => {
        return item.id === payload.item.id
          ? { ...item, name: payload.item.name, category: payload.item.category }
          : item;
      });
    }

    case 'remove': {
      const itemIndex = items.findIndex(item => item.id === payload.item.id);
      if (itemIndex === -1) return items;
      items.splice(itemIndex, 1);
      return [...items];
    }

    default: {
      return items;
    }
  }
};

const progressStates = {
  loading: 'Loading...',
  submitting: 'Submitting...'
} as const;

export const useMockItems = () => {
  return useReducer(itemReducer, undefined, () => initialItems);
};

export const ItemDialog = ({
  target,
  item: itemProp,
  dispatch,
  onComplete
}: MockItemDialogProps) => {
  const [nameField, setNameField] = useState<{
    value: string | null;
    error: typeof errorText | null;
    errorOnPriorBlur: boolean;
  }>(() => {
    return {
      value: itemProp?.name ?? null,
      error: null,
      errorOnPriorBlur: false
    };
  });

  const [categoryField, setCategoryField] = useState<{
    value: MockCategory | null;
    error: typeof errorText | null;
  }>(() => {
    return {
      value: itemProp?.category ?? null,
      error: null
    };
  });

  const [progress, setProgress] = useState<
    (typeof progressStates)[keyof typeof progressStates] | false
  >(progressStates.loading);

  useEffect(() => {
    let timerId: number;

    if (progress === progressStates.loading) {
      timerId = window.setTimeout(() => {
        setProgress(false);
      }, loadingTimeoutMS);
    } else if (progress === progressStates.submitting) {
      timerId = window.setTimeout(() => {
        setProgress(false);
        dispatch({ name: nameField.value!, category: categoryField.value! });
        onComplete();
      }, loadingTimeoutMS);
    }

    return () => {
      window.clearTimeout(timerId);
    };
  }, [progress]);

  return (
    <FormDialog
      target={target}
      heading={`${itemProp ? 'Edit' : 'Add'} item`}
      progress={progress}
      onCancel={{
        disabled: progress === progressStates.submitting,
        handler: () => {
          setProgress(false);
          onComplete();
        }
      }}
      onSubmit={{
        disabled:
          !!progress || [nameField, categoryField].some(field => !field.value || field.error),
        handler: () => {
          setProgress(progressStates.submitting);
        }
      }}
    >
      {(!progress || progress !== progressStates.loading) && (
        <Flex container={{ direction: 'column', gap: 2 }}>
          <Input
            required
            label='Name'
            value={nameField.value ?? ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setNameField(cur => ({
                ...cur,
                value: e.target.value,
                error: !e.target.value && cur.errorOnPriorBlur ? errorText : null
              }));
            }}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              if (!e.target.value) {
                setNameField(cur => ({
                  ...cur,
                  error: errorText,
                  errorOnPriorBlur: true
                }));
              } else if (nameField.errorOnPriorBlur) {
                setNameField(cur => ({ ...cur, errorOnPriorBlur: false }));
              }
            }}
            status={nameField.error ? 'error' : undefined}
            info={nameField.error}
          />
          <ComboBox
            required
            label='Category'
            selected={
              categoryField.value
                ? {
                    items: {
                      id: categoryField.value,
                      text: categoryField.value
                    }
                  }
                : undefined
            }
            menu={{
              mode: 'single-select',
              onItemClick: category => {
                setCategoryField({
                  value: category as MockCategory,
                  error: null
                });
              },
              items: categories.map(category => {
                return {
                  id: category,
                  primary: category,
                  selected: category === categoryField.value
                };
              })
            }}
            onBlur={selected => {
              if (!selected) {
                setCategoryField(cur => ({ ...cur, error: errorText }));
              }
            }}
            status={categoryField.error ? 'error' : undefined}
            info={categoryField.error}
          />
        </Flex>
      )}
    </FormDialog>
  );
};

export const FormDialogMock = () => {
  const [dialogTarget, setDialogTarget] = useState<MockItemDialogProps['target'] | null>(null);
  const [editItem, setEditItem] = useState<MockItem | null>(null);
  const [items, dispatch] = useMockItems();

  return (
    <>
      <SummaryList
        name='Editable items'
        icon='list'
        count={items.length}
        actions={[
          {
            id: 'Add',
            text: 'Add',
            icon: 'plus',
            onClick: (_, e) => {
              setDialogTarget(e.currentTarget);
            }
          }
        ]}
        items={items.map<SummaryListItem>(({ id, name, category }) => {
          return {
            id,
            primary: name,
            secondary: <Text variant='secondary'>{category}</Text>,
            actions: [
              {
                id: 'Edit',
                text: 'Edit',
                icon: 'pencil',
                onClick: (_, e, menuButton) => {
                  if (menuButton) setDialogTarget(menuButton);

                  setEditItem({
                    id,
                    name,
                    category
                  });
                }
              },
              {
                id: 'Remove',
                text: 'Remove',
                icon: 'trash',
                onClick: () => {
                  dispatch({ type: 'remove', item: { id } });
                }
              }
            ]
          };
        })}
      />

      {dialogTarget && (
        <ItemDialog
          target={dialogTarget}
          item={editItem}
          // Wrap dispatch so the dialog can only affect the item it creates or updates.
          dispatch={fields => {
            dispatch(
              editItem
                ? { type: 'edit', item: { id: editItem.id, ...fields } }
                : { type: 'add', item: { ...fields } }
            );
          }}
          onComplete={() => {
            setDialogTarget(null);
            setEditItem(null);
          }}
        />
      )}
    </>
  );
};
